import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { formatMoney, localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import qs from 'qs';
import { Fragment, useState } from 'react';
import EmployeeCard from './components/EmployeeCard';
import EmploymentPeriodsTable from './components/EmploymentPeriodsTable';
import Navigation from './components/Navigation';
import NoteModal from './components/NoteModal';

const SavingsView = () => {
    const { auth, employments, saving_types } = usePage().props;
    const { filters, updateFilters } = useFilters(['employments']);
    const [selectedRow, setSelectedRow] = useState(null);
    const [active, setActive] = useState(null);

    const confirm = useConfirmationStore(state => state.confirm);

    const handleRowSelected = (event, employmentId) => {
        if (
            !event.currentTarget.contains(event.target) ||
            event.target.closest(
                'button, a, input, label, select, textarea, [role="checkbox"]'
            )
        ) {
            return;
        }

        setSelectedRow(prev => (prev === employmentId ? null : employmentId));
    };

    const reopen = contractCheckId =>
        confirm({
            title: 'Contractcontrole heropenen?',
            description:
                'Weet je zeker dat je deze contractcontrole opnieuw wilt uitvoeren? De status wordt teruggezet naar "Contract aangevraagd" en de medewerker verschijnt weer in de lijst met contractcontroles.',
            confirmLabel: 'Opnieuw uitvoeren',
            onConfirm: close => {
                router.patch(
                    `/customers/${auth?.user?.selected_customer_id}/services/ww-check/contract-check/${contractCheckId}`,
                    {
                        status: 1, // EmploymentContractStatus::ContractRequested
                    },
                    {
                        preserveScroll: true,
                        only: [
                            'employments',
                            'business_rules',
                            'contract_checks',
                            'savings',
                        ],
                    }
                );

                close();
            },
        });

    return (
        <Container fluid>
            <Navigation reload={['employments']} />

            <Segment>
                <SegmentHeader
                    header={`${saving_types?.find(t => t.id == filters?.filter)?.name} (${employments?.meta?.total || 0})`}
                >
                    {filters?.filter === 'savings' && (
                        <Button
                            as='a'
                            target='_blank'
                            rel='noopener noreferrer'
                            href={`/customers/${auth?.user?.selected_customer_id}/services/ww-check/savings/exports?${qs.stringify(
                                {
                                    entity_id: filters?.entity_id || undefined,
                                }
                            )}`}
                        >
                            Exporteren
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['employments']}>
                        <FilterBarDropdown
                            label={
                                saving_types?.find(t => t.id == filters?.filter)
                                    ?.name || 'Onbekend'
                            }
                            options={saving_types || []}
                            value={filters?.filter}
                            onChange={value => updateFilters('filter', value)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                <TableHeaderCell>
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell collapsing>
                                    Datum uitdienst
                                </TableHeaderCell>
                                {filters?.filter === 'savings' && (
                                    <TableHeaderCell textAlign='right'>
                                        Besparingen
                                    </TableHeaderCell>
                                )}
                                {filters?.filter !== 'savings' && (
                                    <TableHeaderCell textAlign='right'>
                                        Acties
                                    </TableHeaderCell>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {employments?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={4} />
                            )}

                            {employments?.data?.map(employment => (
                                <Fragment key={employment.id}>
                                    <TableRow
                                        onClick={event =>
                                            handleRowSelected(
                                                event,
                                                employment.id
                                            )
                                        }
                                    >
                                        <TableCell>
                                            <EmployeeCard
                                                employment={employment}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {localDate(employment?.starts_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span>
                                                        {localDate(
                                                            employment.ends_at
                                                        ) || '-'}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {employment.duration}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {filters?.filter === 'savings' &&
                                                formatMoney(
                                                    employment.ww_contract_check
                                                        .savings || 0
                                                )}

                                            <div className='flex items-center justify-end gap-2'>
                                                {filters?.filter ===
                                                    'no_savings' && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                onClick={e => {
                                                                    e.stopPropagation();

                                                                    setActive(
                                                                        employment
                                                                    );
                                                                }}
                                                            >
                                                                <DocumentTextIcon className='size-4' />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {
                                                                employment
                                                                    ?.ww_contract_check
                                                                    ?.note
                                                            }
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {filters?.filter !==
                                                    'savings' && (
                                                    <Button
                                                        onClick={e => {
                                                            e.stopPropagation();

                                                            reopen(
                                                                employment
                                                                    ?.ww_contract_check
                                                                    ?.id
                                                            );
                                                        }}
                                                    >
                                                        Opnieuw uitvoeren
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {selectedRow === employment.id && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <EmploymentPeriodsTable
                                                    year={filters?.year}
                                                    data={
                                                        employment.employment_periods
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={employments?.meta}
                        only={['employments']}
                    />
                </SegmentFooter>
            </Segment>

            {active?.ww_contract_check && (
                <NoteModal
                    key={active.ww_contract_check.id}
                    contractCheck={active.ww_contract_check}
                    open
                    onClose={() => setActive(null)}
                />
            )}
        </Container>
    );
};

export default SavingsView;
